import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { bindAll, omit, isEmpty } from 'mojito/utils';
import { connect } from 'react-redux';
import SearchResultItem from 'applications/sports/components/search/search-result-item/index.js';
import RecentSearch from 'applications/sports/components/search/recent-search/index.js';
import SearchResultGroup from 'applications/sports/components/search/search-result-group/index.js';

const { SearchInput, Popup, FlexPane, AbsolutePane, Text, MenuRow } = MojitoPresentation.Components;
const { UIViewImplementation, bodyScrollManager } = MojitoCore.Presentation;
const searchActions = MojitoServices.Search.actions;
const { RESULT_TYPE } = MojitoServices.Search.types;
const log = MojitoCore.logger.get('SearchView');

const RENDER_MODE = {
    NOTHING: 'nothing',
    PENDING: 'pending',
    PROMPT: 'prompt',
    NOT_ENOUGH_SEARCH_CHARS: 'notEnoughSearchChars',
    NO_RESULTS_FOUND: 'noResultFound',
    SHOW_RESULTS: 'showResults',
};

class SearchView extends UIViewImplementation {
    constructor(props) {
        super(props);

        this.state = {
            active: this.props.initiallyActive,
            searchString: '',
            renderMode: RENDER_MODE.PROMPT,
        };

        this.searchRequestDelay = this.config.searchRequestDelay * 1000;
        this.searchRequestTimer = undefined;

        bindAll(this, [
            'onFocus',
            'onChange',
            'onKeyEnter',
            'onClose',
            'performSearch',
            'search',
            'fetchMoreResults',
            'onRedirection',
            'onRecentSearchItemClick',
        ]);
    }

    componentDidUpdate(prevProps) {
        if (this.props.searchResult.result !== prevProps.searchResult.result) {
            this.setState({
                renderMode: this.getRenderMode(),
            });
        }

        if (this.props.searchInputResetState !== prevProps.searchInputResetState) {
            this.onChange('');
        }
    }

    componentWillUnmount() {
        this.stopSearchRequestDelay();
    }

    getRenderMode() {
        const { result, pending } = this.props.searchResult;
        // If pending, but items are here, let InfiniteList to show animation
        if (!this.state.searchString) {
            return RENDER_MODE.PROMPT;
        }

        if (Object.keys(result).length) {
            return RENDER_MODE.SHOW_RESULTS;
        }
        // If not pending, should be error. Do not show to user, just report no results
        return pending ? RENDER_MODE.PENDING : RENDER_MODE.NO_RESULTS_FOUND;
    }

    haveEnoughSearchChars() {
        return this.state.searchString.length >= this.config.minRequiredChars;
    }

    performSearch() {
        if (this.haveEnoughSearchChars()) {
            this.search(this.state.searchString);
        } else {
            this.setState({ renderMode: RENDER_MODE.NOT_ENOUGH_SEARCH_CHARS });
        }
    }

    onRecentSearchItemClick(searchString) {
        this.emitAnalytics('searchHistoryItemClicked', searchString);
        this.search(searchString);
    }

    search(searchString) {
        this.setState({ searchString, renderMode: RENDER_MODE.PENDING });
        this.stopSearchRequestDelay();
        this.props.dispatch(
            searchActions.search(searchString, this.appContext().systemSettings().language)
        );
    }

    startSearchRequestDelay() {
        this.searchRequestTimer = setTimeout(this.performSearch, this.searchRequestDelay);
    }

    stopSearchRequestDelay() {
        if (this.searchRequestTimer) {
            clearTimeout(this.searchRequestTimer);
            this.searchRequestTimer = undefined;
        }
    }

    renderPlayerGroups(playerGroups) {
        const hasPlayerGroup = !!Object.keys(playerGroups).length;

        if (!hasPlayerGroup) {
            return null;
        }

        const {
            mojitoTools: { stringResolver },
        } = this.props;

        return Object.entries(playerGroups).map(([playerName, searchResult]) => {
            const title = stringResolver.resolveAndFormatString(
                '$SEARCH_VIEW.BET_ON_PLAYER',
                playerName
            );
            return (
                <SearchResultGroup
                    key={playerName}
                    title={title}
                    searchResult={searchResult}
                    onRedirection={this.onRedirection}
                    isPlayerGroup={true}
                />
            );
        });
    }

    getSportItemFromMenu() {
        const { menuItems, selectedSport } = this.props;
        const selectedMenuItem = menuItems.find(item => item.id === selectedSport);
        return {
            id: selectedMenuItem.id,
            name: selectedMenuItem.data,
            resultType: RESULT_TYPE.SPORT,
            sportId: selectedMenuItem.id,
        };
    }

    renderResults() {
        const {
            searchResult: { result },
            menuItems,
            selectedSport,
            onSportClick,
            mojitoTools: { stringResolver },
        } = this.props;

        const sportItem =
            result?.[RESULT_TYPE.SPORT][0] || this.getSportItemFromMenu(selectedSport);
        const events = result?.[RESULT_TYPE.EVENT];
        const competitions = result?.[RESULT_TYPE.TYPE];
        const raceMeetings = result?.[RESULT_TYPE.RACE_MEETING];
        const playerGroups = omit(result, ...Object.values(RESULT_TYPE));

        return (
            <div style={this.style.resultsContainer} className={'ta-resultsContainer'}>
                {!isEmpty(this.props.menuItems) && (
                    <FlexPane config={this.config.menuContainer} class={'ta-menuContainer'}>
                        <MenuRow
                            config={this.config.sportMenu}
                            menuItems={menuItems}
                            selectedItemId={selectedSport}
                            cbItemClicked={onSportClick}
                        />
                    </FlexPane>
                )}
                <FlexPane config={this.config.resultsGroupContainer} class={'ta-groupsContainer'}>
                    {sportItem && (
                        <SearchResultItem {...sportItem} onRedirection={this.onRedirection} />
                    )}

                    {!!competitions?.length && (
                        <SearchResultGroup
                            title={stringResolver.resolveString('$SEARCH_VIEW.COMPETITIONS')}
                            searchResult={competitions}
                            onRedirection={this.onRedirection}
                        />
                    )}

                    {!!events?.length && (
                        <SearchResultGroup
                            title={stringResolver.resolveString('$SEARCH_VIEW.EVENTS')}
                            searchResult={events}
                            onRedirection={this.onRedirection}
                        />
                    )}

                    {!!raceMeetings?.length && (
                        <SearchResultGroup
                            title={stringResolver.resolveString('$SEARCH_VIEW.RACE_MEETINGS')}
                            searchResult={raceMeetings}
                            onRedirection={this.onRedirection}
                        />
                    )}

                    {this.renderPlayerGroups(playerGroups)}
                </FlexPane>
            </div>
        );
    }

    renderMessage(message) {
        return (
            <FlexPane config={this.config.messageContainer}>
                <Text config={this.config.messageText}>{message}</Text>
            </FlexPane>
        );
    }

    renderPrompt() {
        const { recentItems, onRemoveRecentSearch } = this.props;
        if (recentItems.length === 0) {
            return this.renderMessage(this.resolveString('$SEARCH_VIEW.GREETING'));
        }

        return (
            <RecentSearch
                items={recentItems}
                onItemClick={this.onRecentSearchItemClick}
                onItemRemoveClick={onRemoveRecentSearch}
            />
        );
    }

    renderContents() {
        switch (this.state.renderMode) {
            case RENDER_MODE.NOTHING:
                return null;
            case RENDER_MODE.PENDING:
                return null;
            case RENDER_MODE.PROMPT:
                return this.renderPrompt();
            case RENDER_MODE.NOT_ENOUGH_SEARCH_CHARS:
                return this.renderMessage(
                    this.resolveAndFormatString(
                        '$SEARCH_VIEW.NOT_ENOUGH_CHARS',
                        this.config.minRequiredChars
                    )
                );
            case RENDER_MODE.NO_RESULTS_FOUND:
                return this.renderMessage(
                    this.resolveAndFormatString(
                        '$SEARCH_VIEW.NO_RESULTS_FOUND',
                        `'${this.state.searchString}'`
                    )
                );
            case RENDER_MODE.SHOW_RESULTS:
                return this.renderResults();
            default:
                log.error(`renderContents: wrong value of ${this.state.renderMode}`);
        }
    }

    renderResultArea() {
        if (!this.state.active) {
            return null;
        }
        if (this.config.showPopover) {
            return (
                <Popup
                    visible={true}
                    config={this.config.searchResultsPopup}
                    class="ta-searchResultsPopup"
                    captureOutsideClicks={true}
                    onOutsideClick={this.onClose}
                >
                    <FlexPane config={this.config.resultsContainerPopup}>
                        {this.renderContents()}
                    </FlexPane>
                </Popup>
            );
        }
        return (
            <AbsolutePane class="ta-resultsContainer" config={this.config.resultsContainer}>
                {this.renderContents()}
            </AbsolutePane>
        );
    }

    onFocus() {
        !this.state.active && this.emitAnalytics('searchInputFocused');
        this.setState({ active: true });
    }

    onChange(searchString) {
        this.stopSearchRequestDelay();
        this.setState({ searchString });
        if (searchString === '') {
            this.setState({ renderMode: RENDER_MODE.PROMPT });
        } else {
            this.setState({ renderMode: RENDER_MODE.NOTHING });
            this.startSearchRequestDelay();
        }
    }

    onKeyEnter() {
        this.stopSearchRequestDelay();
        this.performSearch();
    }

    onClose() {
        this.props.onSearchClose();
        this.setState({ active: false });
    }

    onRedirection() {
        bodyScrollManager.reset();
        this.props.dispatch(searchActions.addRecentSearch({ query: this.state.searchString }));
        this.onClose();
    }

    render() {
        return (
            <div className="SearchView" style={this.style.searchBox}>
                <SearchInput
                    config={this.config.searchInput}
                    placeholderValue={this.resolveString('$SEARCH_VIEW.INPUT_PLACEHOLDER')}
                    default={this.state.searchString}
                    showSpinner={this.state.renderMode === RENDER_MODE.PENDING}
                    onFocus={this.onFocus}
                    onChange={this.onChange}
                    onKeyEnter={this.onKeyEnter}
                    initiallyActive={this.props.initiallyActive}
                />
                {this.renderResultArea()}
            </div>
        );
    }
}

SearchView.getStyle = function (config, mode, merge) {
    return {
        searchBox: config.style.searchBox,
        resultsContainer: merge(config.style.resultsContainer, {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
        }),
    };
};

export default connect()(SearchView);
